import { useState, useEffect, useCallback, useRef } from 'react';
import './GuardianProtocol.css';

interface GuardianProtocolProps {
  apiBaseUrl: string;
}

const DRAFT_TEXT =
  'This cashmere sweater is our bestseller — it runs true to size and pairs perfectly with our slim-fit chinos.';

const LAYERS = [
  {
    label: 'Brand Voice',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    label: 'Accuracy',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
      </svg>
    ),
  },
  {
    label: 'Compliance',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
      </svg>
    ),
  },
  {
    label: 'Tone',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
      </svg>
    ),
  },
];

type Phase =
  | 'idle'
  | 'draft'
  | 'layers-in'
  | 'validating'
  | 'approved'
  | 'pause'
  | 'fadeout';

export function GuardianProtocol(_props: GuardianProtocolProps) {
  const loopRef = useRef(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(resolve, ms);
        timeoutsRef.current.push(id);
      }),
    [],
  );

  const [phase, setPhase] = useState<Phase>('idle');
  const [layersVisible, setLayersVisible] = useState(false);
  const [activeLayer, setActiveLayer] = useState(-1);
  const [passedLayers, setPassedLayers] = useState<boolean[]>([false, false, false, false]);

  const runLoop = useCallback(async () => {
    while (loopRef.current) {
      setPhase('idle');
      setLayersVisible(false);
      setActiveLayer(-1);
      setPassedLayers([false, false, false, false]);
      await sleep(600);

      if (!loopRef.current) return;
      setPhase('draft');
      await sleep(1000);

      if (!loopRef.current) return;
      setPhase('layers-in');
      setLayersVisible(true);
      await sleep(700);

      if (!loopRef.current) return;
      setPhase('validating');

      for (let i = 0; i < LAYERS.length; i++) {
        if (!loopRef.current) return;
        setActiveLayer(i);
        await sleep(600);
        if (!loopRef.current) return;
        setPassedLayers((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
        setActiveLayer(-1);
        await sleep(300);
      }

      if (!loopRef.current) return;
      setPhase('approved');
      await sleep(3000);

      if (!loopRef.current) return;
      setPhase('fadeout');
      await sleep(900);

      await sleep(400);
    }
  }, [sleep]);

  useEffect(() => {
    loopRef.current = true;
    runLoop();
    return () => {
      loopRef.current = false;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [runLoop]);

  const isVisible = phase !== 'idle';
  const isFading = phase === 'fadeout';
  const isApproved = phase === 'approved';
  const draftBright = isApproved;

  return (
    <div className="guardian-protocol">
      <div
        className={`gp-stage ${isFading ? 'gp-fade-out' : ''}`}
      >
        {/* Draft response bubble */}
        <div
          className={`sc-bubble sc-bubble-assistant gp-draft ${isVisible ? 'gp-draft-visible' : ''} ${draftBright ? 'gp-draft-bright' : ''}`}
        >
          <div className="gp-draft-label sc-section-label">AI Draft Response</div>
          <div className="gp-draft-text">{DRAFT_TEXT}</div>
          {isApproved && (
            <div className="gp-approved-badge">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Approved
            </div>
          )}
        </div>

        {/* Validation layers */}
        <div className={`gp-layers ${layersVisible ? 'gp-layers-visible' : ''}`}>
          <div className="gp-layers-label sc-section-label">Safety Layers</div>
          {LAYERS.map((layer, i) => (
            <div
              key={layer.label}
              className={`gp-layer sc-glass ${activeLayer === i ? 'gp-layer-active' : ''} ${passedLayers[i] ? 'gp-layer-passed' : ''}`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <span className="gp-layer-icon">{layer.icon}</span>
              <span className="gp-layer-label">{layer.label}</span>
              <span className="gp-layer-status">
                {passedLayers[i] && (
                  <span className="sc-check gp-check-enter">✓</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
